const UUID_FRAGMENT_REGEX = /^[0-9a-f]{4,}$/i;
const TIMESTAMP_REGEX = /^\d{6,}$/;

const normalizeBaseName = (filename) => {
  if (!filename || typeof filename !== 'string') {
    return { baseName: '', extension: '' };
  }

  let cleanName = filename;
  if (cleanName.includes('/')) {
    cleanName = cleanName.split('/').pop();
  }
  if (cleanName.includes('\\')) {
    cleanName = cleanName.split('\\').pop();
  }

  const lastDot = cleanName.lastIndexOf('.');
  const extension = lastDot >= 0 ? cleanName.slice(lastDot + 1) : '';
  const baseWithoutExt = lastDot >= 0 ? cleanName.slice(0, lastDot) : cleanName;

  return {
    baseName: baseWithoutExt,
    extension,
    cleanName,
  };
};

const ensureExtension = (name, extension) => {
  if (!name) {
    return null;
  }
  return extension ? `${name}.${extension}` : name;
};

const isUuidFragment = (token) => UUID_FRAGMENT_REGEX.test(token);
const isTimestampFragment = (token) => TIMESTAMP_REGEX.test(token);

export const getDisplayFilename = (filename, stepType = '') => {
  if (!filename) {
    return 'Unknown file';
  }

  const { baseName, extension, cleanName } = normalizeBaseName(filename);
  const lower = cleanName.toLowerCase();
  const normalizedStep = (stepType || '').toLowerCase();

  const prefer = (candidate) => ensureExtension(candidate, extension) || cleanName;

  // Fast paths for well-known extensions
  if (extension === 'obj') {
    return prefer('final_mesh');
  }
  if (extension === 'mtl') {
    return prefer('final_mesh');
  }

  if (normalizedStep.includes('mesh')) {
    if (lower.includes('texture')) {
      return prefer('texture');
    }
    if (lower.includes('material')) {
      return prefer('material');
    }
    if (lower.includes('mesh')) {
      return prefer('final_mesh');
    }
  }

  if (normalizedStep.includes('background')) {
    if (lower.includes('background') && lower.includes('removed')) {
      return prefer('background_removed');
    }
    if (lower.includes('mask')) {
      return prefer('mask');
    }
    if (lower.includes('original')) {
      return prefer('original');
    }
  }

  if (normalizedStep.includes('depth')) {
    if (lower.includes('normalized')) {
      return prefer('depth_normalized');
    }
    if (lower.includes('raw')) {
      return prefer('depth_raw');
    }
    if (lower.includes('depth')) {
      return prefer('depth');
    }
  }

  if (normalizedStep.includes('pointcloud')) {
    if (lower.includes('point')) {
      return prefer('pointcloud');
    }
  }

  // General keyword detection fallback
  if (lower.includes('background') && lower.includes('removed')) {
    return prefer('background_removed');
  }
  if (lower.includes('normalized')) {
    return prefer('depth_normalized');
  }
  if (lower.includes('depth') && lower.includes('raw')) {
    return prefer('depth_raw');
  }
  if (lower.includes('texture')) {
    return prefer('texture');
  }
  if (lower.includes('original')) {
    return prefer('original');
  }

  // Strip UUID / timestamp fragments and recombine remaining tokens
  const tokens = baseName.split(/[_-]+/)
    .map((token) => token.trim())
    .filter((token) => token.length > 0);

  const meaningful = tokens.filter((token) => {
    const lowerToken = token.toLowerCase();
    if (lowerToken === 'tmp' || lowerToken === 'temp') {
      return false;
    }
    if (isUuidFragment(token) || isTimestampFragment(token)) {
      return false;
    }
    return true;
  });

  if (meaningful.length > 0) {
    return prefer(meaningful.join('_'));
  }

  return cleanName;
};

export const formatFileSize = (bytes, fallbackLabel = 'Unknown size') => {
  if (bytes === null || bytes === undefined) {
    return fallbackLabel;
  }

  const size = Number(bytes);
  if (Number.isNaN(size) || size <= 0) {
    return fallbackLabel;
  }

  if (size < 1024) {
    return `${size} B`;
  }
  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }
  if (size < 1024 * 1024 * 1024) {
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  }

  return `${(size / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

export const normalizeFileMetadata = (file, stepType) => {
  if (!file) {
    return null;
  }

  const filenameInput = file.filename || file.name || '';
  const { cleanName } = normalizeBaseName(filenameInput);

  const rawSize =
    file.file_size !== undefined ? file.file_size :
    file.size !== undefined ? file.size :
    file.file_size_bytes !== undefined ? file.file_size_bytes :
    file.bytes !== undefined ? file.bytes : null;

  const parsedSize = typeof rawSize === 'string' ? Number(rawSize) : rawSize;
  const fileSize = Number.isFinite(parsedSize) && parsedSize > 0 ? parsedSize : null;

  const resolvedStepType = stepType || file.stepType || null;

  const displayName = getDisplayFilename(filenameInput, resolvedStepType);
  const canonicalName = (displayName || cleanName || filenameInput || '').toLowerCase();

  return {
    ...file,
    filename: filenameInput,
    canonical_name: canonicalName,
    stepType: resolvedStepType,
    file_size: fileSize,
    display_name: displayName,
  };
};

